/**
 * Lets the shared wind-particles.js (written for MapLibre, used verbatim by iOS, Android
 * and our 3D map) run over the 2D Google map. It only needs a handful of MapLibre calls:
 * getCanvas() for the size, getCanvasContainer() to hold its canvas, getZoom(), unproject()
 * and the movestart / moveend / resize events: the surface the three platforms agreed the
 * module may use. This object answers them from Google Maps.
 *
 * - unproject goes through an empty OverlayView's projection, so tilt and heading on the
 *   vector map are handled the same way MapLibre handles pitch and bearing.
 * - movestart is bounds_changed (the first one of a gesture fades the streaks out; the
 *   rest are no-ops) and moveend is idle, which is when the streaks re-seed.
 */
const adapters = new WeakMap()

export function googleWindMap(gmap) {
  if (adapters.has(gmap)) return adapters.get(gmap)

  const div = gmap.getDiv()
  const listeners = { movestart: new Map(), moveend: new Map(), resize: new Map() }
  const googleEvent = { movestart: 'bounds_changed', moveend: 'idle' }

  // The projection only exists once the overlay is on the map. Particles seeded before
  // that find no data, so tell them the camera settled as soon as it is ready.
  const overlay = new google.maps.OverlayView()
  let ready = false
  overlay.onAdd = () => {}
  overlay.onRemove = () => {}
  overlay.draw = () => {
    if (ready) return
    ready = true
    listeners.moveend.forEach((_, fn) => fn())
  }
  overlay.setMap(gmap)

  const adapter = {
    getCanvas: () => div,
    getCanvasContainer: () => div,
    // MapLibre zoom, which the module's fades are written in: Google's 256 px tiles put
    // it one level above MapLibre's 512 px tiles at the same scale.
    getZoom: () => gmap.getZoom() - 1,
    unproject([x, y]) {
      const projection = ready && overlay.getProjection()
      const ll = projection && projection.fromContainerPixelToLatLng(new google.maps.Point(x, y))
      return ll ? { lng: ll.lng(), lat: ll.lat() } : { lng: NaN, lat: NaN }
    },
    on(name, fn) {
      const set = listeners[name]
      if (!set || set.has(fn)) return
      // Google's resize event is gone; a resized map div sends bounds_changed and idle.
      set.set(fn, googleEvent[name] ? google.maps.event.addListener(gmap, googleEvent[name], () => fn()) : null)
    },
    off(name, fn) {
      const set = listeners[name]
      if (!set || !set.has(fn)) return
      const handle = set.get(fn)
      if (handle) google.maps.event.removeListener(handle)
      set.delete(fn)
    }
  }
  adapters.set(gmap, adapter)
  return adapter
}
